import { Link } from "react-router";
import { ArrowRight, Award, Leaf, Recycle, Target, TreePine, Droplets, Factory, Zap, CheckCircle2 } from "lucide-react";
import { useInViewOnce } from "../hooks/useInViewOnce";
import { cn } from "../components/ui/utils";

export function SustainabilityPage() {
  const { ref: headlineRef, inView: headlineInView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* HERO SECTION */}
      <section className="relative py-32 bg-[#F6F2EB] overflow-hidden min-h-[768px] flex items-center">
        {/* Falling Leaves Animation Layer (Original 7 Leaves) */}
        <div className="absolute inset-0 pointer-events-none select-none">
          <style>{`
            @keyframes leaf-fall-vertical {
              0% { top: -25%; opacity: 1; }
              100% { top: 125%; opacity: 1; }
            }
            @keyframes leaf-fall-sway {
              0%, 100% { 
                transform: translateX(calc(var(--sway-w) * -1)) rotate(calc(var(--base-rot) + var(--rot-i))); 
              }
              50% { 
                transform: translateX(var(--sway-w)) rotate(calc(var(--base-rot) - var(--rot-i))); 
              }
            }
            .falling-leaf {
              position: absolute;
              background-image: url(/leaf.png);
              background-size: contain;
              background-repeat: no-repeat;
              top: -25%;
              opacity: 0;
              animation: leaf-fall-vertical ease-out 1 both, leaf-fall-sway ease-in-out infinite;
            }
            @keyframes text-reveal {
              0% { opacity: 0; filter: blur(10px); transform: translateY(20px); }
              100% { opacity: 1; filter: blur(0); transform: translateY(0); }
            }
            .sustain-text {
              background-image: url(/sustain.png);
              background-size: cover;
              background-position: 50% 35%;
              -webkit-background-clip: text;
              background-clip: text;
              color: transparent;
              display: inline-block;
            }
          `}</style>
        </div>

        {/* Background Leaves (Behind Headline) */}
        <div className="absolute inset-0 pointer-events-none select-none z-0">
          {[
            { w: '154.41px', h: '115.76px', l: '75%', r: '60deg', d: '0.1s', f: '5.2s', sw: '70px', si: '22deg', sd: '2.4s' },
            { w: '182.49px', h: '136.82px', l: '50%', r: '-97.62deg', d: '0.2s', f: '7.8s', sw: '90px', si: '30deg', sd: '3.0s' },
            { w: '64.41px', h: '48.29px', l: '25%', r: '-91.29deg', d: '0.05s', f: '6.5s', sw: '30px', si: '10deg', sd: '2.0s' },
          ].map((leaf, i) => (
            <div
              key={`bg-${i}`}
              className="falling-leaf"
              style={{
                width: leaf.w,
                height: leaf.h,
                left: leaf.l,
                animationDuration: `${leaf.f}, ${leaf.sd}`,
                animationDelay: `${leaf.d}, 0s`,
                '--base-rot': leaf.r,
                '--sway-w': leaf.sw,
                '--rot-i': leaf.si
              } as any}
            />
          ))}
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-[#FBF7F4] text-[#131312] border border-[#131312]/10 rounded-full text-sm font-medium mb-8 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.15)]">
              Environmental Responsibility
            </div>
            <h1 ref={headlineRef} className="text-[48px] sm:text-[60px] md:text-[70px] lg:text-[78.1px] font-bold mb-8 leading-tight text-[#1C1C1C]">
              <span className="sustain-text">Sustainable</span>{' '}
              <span className="text-[#131312]">
                Packaging for a Better Future
              </span>
            </h1>
            <p
              className="font-bold leading-[25px] text-[#1C1C1C] mx-auto"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '18.6px',
                maxWidth: '627px',
                letterSpacing: '-0.5px'
              }}
            >
              We are committed to reducing environmental impact through responsible materials, ethical sourcing, and efficient production processes.
            </p>
          </div>
        </div>

        {/* Foreground Leaves (In Front of Content) */}
        <div className="absolute inset-0 pointer-events-none select-none z-50">
          {[
            { w: '77.96px', h: '58.45px', l: '40%', r: '168.36deg', d: '0.6s', f: '6.2s', sw: '40px', si: '12deg', sd: '2.2s' },
            { w: '92.14px', h: '69.08px', l: '65%', r: '79.34deg', d: '0.8s', f: '5.8s', sw: '50px', si: '15deg', sd: '2.4s' },
            { w: '104.16px', h: '78.09px', l: '85%', r: '-91.29deg', d: '0.5s', f: '4.2s', sw: '75px', si: '25deg', sd: '2.6s' },
            { w: '176.06px', h: '132px', l: '15%', r: '1.44deg', d: '0.4s', f: '7.2s', sw: '60px', si: '20deg', sd: '2.6s' },
          ].map((leaf, i) => (
            <div
              key={`fg-${i}`}
              className="falling-leaf"
              style={{
                width: leaf.w,
                height: leaf.h,
                left: leaf.l,
                animationDuration: `${leaf.f}, ${leaf.sd}`,
                animationDelay: `${leaf.d}, 0s`,
                '--base-rot': leaf.r,
                '--sway-w': leaf.sw,
                '--rot-i': leaf.si
              } as any}
            />
          ))}
        </div>
      </section>

      {/* OUR COMMITMENT */}
      <section className="py-24 bg-[#FBFAF7]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2
                className="text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight mb-6 text-[#131312] leading-tight"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  maxWidth: '568px',
                  letterSpacing: '0.35px'
                }}
              >
                We don't just <br className="hidden md:block" />
                Manufacture Packaging <br className="hidden md:block" />
                We design it <br className="hidden md:block" />
                Responsibly.
              </h2>
            </div>
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="size-3 bg-[#131312] rounded-full mt-2 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Sustainable Materials</h3>
                  <p className="text-muted-foreground">FSC-certified papers, recycled content, and biodegradable options</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="size-3 bg-[#131312] rounded-full mt-2 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Waste Reduction</h3>
                  <p className="text-muted-foreground">85% recycling rate with optimized production to minimize waste</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="size-3 bg-[#131312] rounded-full mt-2 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Ethical Sourcing</h3>
                  <p className="text-muted-foreground">Transparent supply chains with verified ethical practices</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="size-3 bg-[#131312] rounded-full mt-2 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Energy Efficiency</h3>
                  <p className="text-muted-foreground">Modern equipment and processes that reduce energy consumption</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS SECTION */}
      <section className="py-24 bg-[#F6F2EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-left mb-16">
            <h2
              className="text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight mb-4 text-[#131312] leading-tight"
              style={{
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.35px'
              }}
            >
              Certifications & Compliance
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Our commitment to quality and ethics is verified by international standards and independent audits.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* FSC */}
            <div className="flex flex-col h-full bg-white rounded-3xl overflow-hidden border border-[#131312]/10 transition-all hover:shadow-xl group">
              <div className="h-48 flex items-center justify-center bg-white/50">
                <img src="/FSC.png" alt="FSC Logo" className="h-24 w-auto object-contain" />
              </div>
              <div className="p-8 flex flex-col flex-grow">
                <h3 className="text-2xl font-semibold mb-4">FSC Certified</h3>
                <p className="text-muted-foreground mb-8 flex-grow">
                  Forest Stewardship Council certification ensures all our wood and paper products come from responsibly managed forests.
                  <br /><br />
                  <span className="text-xs">
                    FSC Chain of Custody certification is held by Paper Printing Services, the legal entity operating the Colour Club Printers brand.
                  </span>
                </p>
                <div className="space-y-3">
                  {[
                    "Responsible forest management",
                    "Chain of custody verified",
                    "Independent third-party audited"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm font-medium text-[#131312]">
                      <CheckCircle2 className="size-5 text-[#131312] flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ISO 9001 */}
            <div className="flex flex-col h-full bg-white rounded-3xl overflow-hidden border border-[#131312]/10 transition-all hover:shadow-xl group">
              <div className="h-48 flex items-center justify-center bg-white/50">
                <img src="/iso9001.webp" alt="ISO 9001 Logo" className="h-28 w-auto object-contain" />
              </div>
              <div className="p-8 flex flex-col flex-grow">
                <h3 className="text-2xl font-semibold mb-4">ISO 9001</h3>
                <p className="text-muted-foreground mb-8 flex-grow">
                  International standard for quality management systems, demonstrating our commitment to consistent excellence.
                </p>
                <div className="space-y-3">
                  {[
                    "Quality management systems",
                    "Process optimization",
                    "Customer satisfaction focus"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm font-medium text-[#131312]">
                      <CheckCircle2 className="size-5 text-[#131312] flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* SEDEX */}
            <div className="flex flex-col h-full bg-white rounded-3xl overflow-hidden border border-[#131312]/10 transition-all hover:shadow-xl group">
              <div className="h-48 flex items-center justify-center bg-white/50">
                <img src="/sedex.png" alt="Sedex Logo" className="h-16 w-auto object-contain" />
              </div>
              <div className="p-8 flex flex-col flex-grow">
                <h3 className="text-2xl font-semibold mb-4">Sedex Member</h3>
                <p className="text-muted-foreground mb-8 flex-grow">
                  Commitment to ethical and responsible business practices throughout our supply chain and operations.
                </p>
                <div className="space-y-3">
                  {[
                    "Ethical labor practices",
                    "Health and safety standards",
                    "Supply chain transparency"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm font-medium text-[#131312]">
                      <CheckCircle2 className="size-5 text-[#131312] flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ECO-FRIENDLY MATERIALS */}
      <section className="py-24 bg-[#FFFFFF]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-left mb-16">
            <h2
              className="font-bold mb-4 text-[#131312]"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '48px',
                lineHeight: '60px',
                letterSpacing: '0.35px'
              }}
            >
              Sustainable Materials We Use
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Every material we select is chosen for its environmental credentials and performance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
            <div className="flex items-start gap-6">
              <div className="icon-box size-16">
                <Recycle className="size-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3">Recycled Paperboard</h3>
                <p className="text-muted-foreground">
                  100% post-consumer recycled content reduces waste and environmental impact while maintaining premium quality.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="icon-box size-16">
                <TreePine className="size-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3">FSC-Certified Sources</h3>
                <p className="text-muted-foreground">
                  All virgin materials sourced from responsibly managed forests with full chain of custody verification.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="icon-box size-16">
                <Leaf className="size-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3">Kraft Materials</h3>
                <p className="text-muted-foreground">
                  Natural brown kraft with authentic eco-appeal, perfect for brands emphasizing sustainability.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="icon-box size-16">
                <Droplets className="size-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3">Low-Impact Inks</h3>
                <p className="text-muted-foreground">
                  Water-based, low-VOC inks that are non-toxic and ensure safer recycling processes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESPONSIBLE PRODUCTION */}
      <section className="py-24 bg-[#F6F2EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-left mb-16">
            <h2
              className="text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight mb-4 text-[#131312] leading-tight"
              style={{
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.35px'
              }}
            >
              Smarter Manufacturing, <br />
              Lower Impact
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Our production processes are optimized for efficiency and minimal environmental footprint.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="icon-box size-20 mx-auto mb-6 bg-white border border-[#131312]/5 shadow-sm">
                <Target className="size-10" />
              </div>
              <h3 className="text-lg font-bold mb-3">Efficient Material Usage</h3>
              <p className="text-sm text-muted-foreground">
                Advanced nesting algorithms minimize material waste during production
              </p>
            </div>

            <div className="text-center">
              <div className="icon-box size-20 mx-auto mb-6 bg-white border border-[#131312]/5 shadow-sm">
                <Recycle className="size-10" />
              </div>
              <h3 className="text-lg font-bold mb-3">Waste Reduction Systems</h3>
              <p className="text-sm text-muted-foreground">
                85% of production waste is recycled or repurposed
              </p>
            </div>

            <div className="text-center">
              <div className="icon-box size-20 mx-auto mb-6 bg-white border border-[#131312]/5 shadow-sm">
                <Award className="size-10" />
              </div>
              <h3 className="text-lg font-bold mb-3">Quality Control</h3>
              <p className="text-sm text-muted-foreground">
                Rigorous checks reduce reprints and material waste
              </p>
            </div>

            <div className="text-center">
              <div className="icon-box size-20 mx-auto mb-6 bg-white border border-[#131312]/5 shadow-sm">
                <Zap className="size-10" />
              </div>
              <h3 className="text-lg font-bold mb-3">Optimized Cycles</h3>
              <p className="text-sm text-muted-foreground">
                Modern equipment maximizes output while minimizing energy use
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ROADMAP - POWER SECTION */}
      <section className="py-32 bg-[#FBFAF7]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-left mb-20">
            <h2
              className="text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight mb-4 text-[#131312] leading-tight"
              style={{
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.35px'
              }}
            >
              Our Sustainability Goals & <br />
              Roadmap
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              A clear path forward with measurable commitments and transparent progress tracking.
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0 border-l-4 border-dotted border-[#131312]/30 hidden lg:block" />

            <div className="space-y-16">
              <div className="relative pl-0 lg:pl-24">
                <div className="absolute left-0 top-0 size-16 bg-[#131312] text-[#F6F2EB] rounded-2xl flex items-center justify-center font-bold text-xl hidden lg:flex">
                  2025
                </div>
                <div
                  className="bg-[#F6F2EB] rounded-3xl p-8 flex flex-col justify-center border border-[#131312]/5 shadow-sm"
                  style={{
                    width: '100%',
                    maxWidth: '1216px',
                    minHeight: '184px',
                    flex: 'none',
                    alignSelf: 'stretch',
                    flexGrow: 0
                  }}
                >
                  <div className="text-2xl font-semibold mb-4 lg:hidden text-[#131312]">2025</div>
                  <h3 className="text-2xl font-semibold mb-4 text-[#131312]">Foundation & Transparency</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                    <div className="flex items-start gap-3 text-[#131312]">
                      <CheckCircle2 className="size-6 text-[#131312] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold mb-1">Material Sourcing Transparency</div>
                        <div className="text-sm opacity-90 text-[#131312]">Full traceability of all material origins</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-[#131312]">
                      <CheckCircle2 className="size-6 text-[#131312] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold mb-1">Waste Reduction Target</div>
                        <div className="text-sm opacity-90 text-[#131312]">Reduce production waste by 15%</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative pl-0 lg:pl-24">
                <div className="absolute left-0 top-0 size-16 bg-[#131312] text-[#F6F2EB] rounded-2xl flex items-center justify-center font-bold text-xl hidden lg:flex">
                  2026
                </div>
                <div
                  className="bg-[#F6F2EB] rounded-3xl p-8 flex flex-col justify-center border border-[#131312]/5 shadow-sm"
                  style={{
                    width: '100%',
                    maxWidth: '1216px',
                    minHeight: '184px',
                    flex: 'none',
                    alignSelf: 'stretch',
                    flexGrow: 0
                  }}
                >
                  <div className="text-2xl font-semibold mb-4 lg:hidden text-[#131312]">2026</div>
                  <h3 className="text-2xl font-semibold mb-4 text-[#131312]">Innovation & Expansion</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                    <div className="flex items-start gap-3 text-[#131312]">
                      <CheckCircle2 className="size-6 text-[#131312] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold mb-1">Recyclable Options</div>
                        <div className="text-sm opacity-90 text-[#131312]">Expand range of fully recyclable packaging</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-[#131312]">
                      <CheckCircle2 className="size-6 text-[#131312] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold mb-1">Energy Optimization</div>
                        <div className="text-sm opacity-90 text-[#131312]">20% reduction in energy consumption</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative pl-0 lg:pl-24">
                <div className="absolute left-0 top-0 size-16 bg-[#131312] text-[#F6F2EB] rounded-2xl flex items-center justify-center font-bold text-xl hidden lg:flex">
                  2027
                </div>
                <div
                  className="bg-[#F6F2EB] rounded-3xl p-8 flex flex-col justify-center border border-[#131312]/5 shadow-sm"
                  style={{
                    width: '100%',
                    maxWidth: '1216px',
                    minHeight: '184px',
                    flex: 'none',
                    alignSelf: 'stretch',
                    flexGrow: 0
                  }}
                >
                  <div className="text-2xl font-semibold mb-4 lg:hidden text-[#131312]">2027</div>
                  <h3 className="text-2xl font-semibold mb-4 text-[#131312]">Leadership & Impact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                    <div className="flex items-start gap-3 text-[#131312]">
                      <CheckCircle2 className="size-6 text-[#131312] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold mb-1">Carbon-Conscious Production</div>
                        <div className="text-sm opacity-90 text-[#131312]">Move towards carbon-neutral operations</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-[#131312]">
                      <CheckCircle2 className="size-6 text-[#131312] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold mb-1">Eco Product Line</div>
                        <div className="text-sm opacity-90 text-[#131312]">50% of catalog as sustainable options</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative pl-0 lg:pl-24">
                <div className="absolute left-0 top-0 size-16 bg-[#F6F2EB] text-[#131312] border border-[#131312]/10 rounded-2xl flex items-center justify-center font-bold text-xl hidden lg:flex shadow-sm">
                  2030
                </div>
                {/* Gradient Border Wrapper */}
                <div
                  className="rounded-[26px] p-[2px] shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, #FFA200, #996100)',
                    width: '100%',
                    maxWidth: '1120px'
                  }}
                >
                  <div
                    className="bg-[#F6F2EB] flex flex-col justify-center items-start"
                    style={{
                      width: '100%',
                      minHeight: '192px',
                      height: 'auto',
                      padding: '42px',
                      borderRadius: '24px',
                      boxSizing: 'border-box',
                      gap: '16px',
                      flex: 'none',
                      order: 0,
                      alignSelf: 'stretch',
                      flexGrow: 0
                    }}
                  >
                    <div className="text-2xl font-semibold lg:hidden text-[#131312]">2030</div>
                    <h3 className="text-2xl font-semibold text-[#131312] m-0">Carbon Neutral Vision</h3>
                    <p className="text-lg opacity-90 text-[#131312] max-w-3xl m-0 leading-relaxed">
                      Achieve carbon neutrality across all operations through renewable energy,
                      process optimization, and verified carbon offset programs.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS - CLIENT FOCUSED */}
      <section className="py-24 bg-[#F6F2EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-left mb-16">
            <h2
              className="text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight mb-4 text-[#131312] leading-tight"
              style={{
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.35px'
              }}
            >
              Why Sustainability Matters <br />
              for Your Brand
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Partnering with a sustainable supplier strengthens your brand and meets the expectations of today's conscious consumers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="flex items-start gap-6">
              <div className="icon-box size-16 bg-white border border-[#131312]/5 shadow-sm">
                <Award className="size-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3">Global Compliance</h3>
                <p className="text-muted-foreground">
                  Meet UK, EU, and international retail standards for sustainable packaging and ethical sourcing.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="icon-box size-16 bg-white border border-[#131312]/5 shadow-sm">
                <Target className="size-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3">Improved Brand Image</h3>
                <p className="text-muted-foreground">
                  Demonstrate environmental responsibility that resonates with eco-conscious customers and partners.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="icon-box size-16 bg-white border border-[#131312]/5 shadow-sm">
                <Leaf className="size-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3">Eco-Conscious Customers</h3>
                <p className="text-muted-foreground">
                  Connect with consumers who prioritize sustainability and reward brands that share their values.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="icon-box size-16 bg-white border border-[#131312]/5 shadow-sm">
                <Factory className="size-8" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-3">Retail Standards</h3>
                <p className="text-muted-foreground">
                  Align with major retailer requirements for ethical sourcing and environmental responsibility.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
